/**
 * RebookFormFooter — the rebook drawer's confirm row (BON-146).
 *
 * The note on the left is the drawer's running verdict, and the CTA changes
 * wording when an override is in play: "Override & move" rather than "Move
 * booking", so the last thing the CSR reads before committing says what is
 * unusual about this one. There is no confirmation step after it.
 */
export interface RebookFormFooterProps {
  /** Something on screen makes the move impossible. */
  blocked: boolean;
  /** Past the cutoff, permitted, and the box is ticked. */
  overriding: boolean;
  /** A destination has been chosen. */
  hasTarget: boolean;
  submitting: boolean;
  onCancel: () => void;
}

function footNote(blocked: boolean, overriding: boolean, hasTarget: boolean): string {
  if (blocked) return 'This booking can’t be moved — see above.';
  if (overriding) return 'Overriding the 10 AM cutoff. This is recorded.';
  if (hasTarget) return 'Same price — nothing to charge or refund.';
  return 'Pick a workshop to move this booking to.';
}

export function RebookFormFooter({
  blocked,
  overriding,
  hasTarget,
  submitting,
  onCancel,
}: RebookFormFooterProps) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-stone-200 px-6 py-4 dark:border-stone-700">
      <p className="text-xs text-stone-500 dark:text-stone-400">
        {footNote(blocked, overriding, hasTarget)}
      </p>
      <div className="flex flex-shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-stone-600 dark:text-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={blocked || submitting}
          className="rounded-lg bg-moss-600 px-4 py-2 text-sm font-semibold text-white hover:bg-moss-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Moving…' : overriding ? 'Override & move' : 'Move booking'}
        </button>
      </div>
    </div>
  );
}
